import { useRef, useEffect, useState } from "react";
import DailyIframe from "@daily-co/daily-js";

const ERROR_MESSAGES = {
  "account-missing-payment-method":
    "The video service account needs a payment method on file. Go to dashboard.daily.co → Billing and add a card (the free plan stays free — Daily just requires a card to activate calls).",
  "nbf-room": "This room isn't open yet. Please come back closer to the scheduled time.",
  "exp-room": "This consult's time window has ended.",
};

/**
 * Embedded Daily.co video call (Daily Prebuilt UI).
 * Room + token come from our backend - the Daily API key never touches the browser.
 */
export default function DailyVideoFrame({ roomUrl, token, onLeave }) {
  const containerRef = useRef(null);
  const [callError, setCallError] = useState(null);

  useEffect(() => {
    if (!containerRef.current || !roomUrl || !token) return;
    let frame = null;
    let cancelled = false;
    let errored = false;

    const setup = async () => {
      // Destroy any lingering instance (React StrictMode double-mount, fast nav)
      const existing = DailyIframe.getCallInstance?.();
      if (existing) {
        try {
          await existing.destroy();
        } catch (e) {
          /* noop */
        }
      }
      if (cancelled || !containerRef.current) return;
      frame = DailyIframe.createFrame(containerRef.current, {
        iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "12px" },
        showLeaveButton: true,
        showFullscreenButton: true,
      });
      frame.on("error", (e) => {
        errored = true;
        const msg = ERROR_MESSAGES[e?.errorMsg] || `Video call error: ${e?.errorMsg || "unknown"}`;
        setCallError(msg);
      });
      frame.on("left-meeting", () => {
        if (!errored && onLeave) onLeave();
      });
      try {
        await frame.join({ url: roomUrl, token });
      } catch (err) {
        errored = true;
        const msg = ERROR_MESSAGES[err?.errorMsg] || "Could not join the video call. Please try again.";
        setCallError(msg);
      }
    };
    setup();

    return () => {
      cancelled = true;
      if (frame) {
        try {
          frame.destroy();
        } catch (e) {
          /* already destroyed */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomUrl, token]);

  if (callError) {
    return (
      <div
        data-testid="daily-video-error"
        className="w-full h-[70vh] bg-[#0B1726] rounded-xl flex items-center justify-center p-8"
      >
        <p className="text-white font-body text-lg text-center max-w-xl leading-relaxed">{callError}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="daily-video-container"
      className="w-full h-[70vh] bg-[#0B1726] rounded-xl overflow-hidden"
    />
  );
}
